import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import apiService, {
  setInMemoryToken,
  authenticatedFetch,
  authenticatedFetchWithRetry,
  isAuthFailure,
  isBackendUnavailable,
  BOOTSTRAP_RETRY_WINDOW_MS,
} from '../services/api';
import offlineDb from '../services/offlineDb';
import { useSubscription, SubscriptionStatus } from '../hooks/useSubscription';
import { BusinessHours } from '../utils/businessHours';
import i18n from '../i18n';

import logger from '../utils/logger';
interface User {
  id: number;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  role?: string;
  patientId?: number | null;
  shareCalendarWithClinic?: boolean;
  showSharedCalendars?: boolean;
}

interface TenantSettings {
  defaultWhatsappReminder: boolean;
  defaultEmailReminder: boolean;
  businessHours: BusinessHours | null;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  tenantSettings: TenantSettings | null;
  login: (username: string, password: string, totpCode?: string) => Promise<boolean | 'requires_totp'>;
  loginWithData: (token: string, user: User) => void;
  register: (userData: RegisterData) => Promise<boolean>;
  logout: (message?: string) => void;
  logoutWithoutMessage: () => void;
  updateUser: (patch: Partial<User>) => void;
  loading: boolean;
  /** Backend unreachable, session preserved and being revalidated. */
  backendDegraded: boolean;
  subscription: SubscriptionStatus;
  refreshSubscription: () => Promise<void>;
  refreshTenantSettings: () => Promise<void>;
}

interface RegisterData {
  username: string;
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone?: string;
}

const USER_CACHE_KEY = 'nice-agenda-user';

// Store only non-sensitive, display-purpose fields in localStorage.
// We never store the JWT token here — that lives in an httpOnly cookie.
function safeSetUserCache(user: User) {
  if (typeof window === 'undefined') return;
  try {
    const minimal = { id: user.id, firstName: user.firstName, lastName: user.lastName, role: user.role, patientId: user.patientId ?? null };
    localStorage.setItem(USER_CACHE_KEY, JSON.stringify(minimal));
  } catch { /* quota exceeded or private browsing — ignore */ }
}

function safeGetUserCache(): User | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(USER_CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function safeRemoveUserCache() {
  if (typeof window === 'undefined') return;
  try { localStorage.removeItem(USER_CACHE_KEY); } catch { /* ignore */ }
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [tenantSettings, setTenantSettings] = useState<TenantSettings | null>(null);
  // True while the backend is unreachable but the session is still presumed
  // valid — set during a deploy, cleared once /api/auth/profile answers again.
  const [backendDegraded, setBackendDegraded] = useState(false);

  const tenantSettingsFromResponse = (data: any): TenantSettings | null => {
    if (data && data.tenant) {
      return {
        defaultWhatsappReminder: data.tenant.defaultWhatsappReminder ?? true,
        defaultEmailReminder: data.tenant.defaultEmailReminder ?? true,
        businessHours: data.tenant.businessHours ?? null,
      };
    }
    return null;
  };
  const { subscription, refresh: refreshSubscription } = useSubscription(!!token);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const oauthError = params.get('auth_error');
    const oauthToken = params.get('auth_token');

    if (oauthError || oauthToken) {
      if (oauthError) {
        const messages: Record<string, string> = {
          not_registered: 'No account found for this Google address. Ask your administrator to add you first.',
          google_denied: 'Google sign-in was cancelled.',
          invalid_state: 'Security check failed. Please try again.',
          oauth_failed: 'Google sign-in failed. Please try again.',
        };
        // Distinct title from a real session-expiry event — Login.tsx falls
        // back to "Session Expired" when this key is absent.
        localStorage.setItem('tokenExpiryTitle', 'signInFailed');
        localStorage.setItem('tokenExpiryMessage', messages[oauthError] || 'Google sign-in failed.');
      }
      if (oauthToken) {
        // Token from Google OAuth redirect — set in memory so the profile
        // fetch below sends it as Authorization header (belt-and-suspenders
        // alongside the httpOnly cookie, handles cross-domain cookie failures).
        setInMemoryToken(oauthToken);
      }
      window.history.replaceState({}, '', window.location.pathname);
    }

    // Restore session: after Google OAuth the backend sets an httpOnly cookie
    // AND includes auth_token in the redirect URL. We use whichever works.
    const cachedUser = safeGetUserCache();
    if (cachedUser) {
      setUser(cachedUser);
    }

    // Session restore has to survive a deploy. Three outcomes, not two:
    //   200 + user            → session is good
    //   401/403 + auth code   → session is genuinely dead, log out
    //   5xx / offline / throw → backend is down, we don't know: KEEP the
    //                           session, go degraded, and revalidate later.
    // Treating the third case as "logged out" is what used to sign everyone
    // out on every deploy, since a proxy 502 is a perfectly valid response
    // and never reaches .catch().
    const clearSession = () => {
      safeRemoveUserCache();
      setUser(null);
      setToken(null);
      setTenantSettings(null);
      setInMemoryToken(null);
    };

    const enterDegraded = () => {
      const cached = safeGetUserCache();
      if (cached) {
        // Keep them signed in on the cookie; the reconnect watcher revalidates.
        setUser(cached);
        setToken('cookie');
        setBackendDegraded(true);
      } else {
        clearSession();
      }
    };

    authenticatedFetchWithRetry('/api/auth/profile', {}, BOOTSTRAP_RETRY_WINDOW_MS)
      .then(async (res) => {
        const data = await res.json().catch(() => null);

        if (res.ok && data?.user) {
          // Always prefer the fresh token issued by /profile over the original
          // OAuth URL token. The profile endpoint signs a new 7d JWT and
          // refreshes the httpOnly cookie — keeping _inMemoryToken in sync with
          // the cookie prevents "session expired" mismatches on next day usage.
          const freshToken = data.token || oauthToken || null;
          setInMemoryToken(freshToken);
          setToken(freshToken || 'cookie');
          setUser(data.user);
          setTenantSettings(tenantSettingsFromResponse(data));
          safeSetUserCache(data.user);
          setBackendDegraded(false);
        } else if (isAuthFailure(res.status, data)) {
          clearSession();
        } else if (isBackendUnavailable(res.status, data)) {
          logger.warn('Backend unavailable during session restore — keeping session, will revalidate.');
          enterDegraded();
        } else {
          // A decisive non-auth error (e.g. 400/404): nothing to keep.
          clearSession();
        }
        setLoading(false);
      })
      .catch(() => {
        // Network error (backend restart, brief outage) — don't logout.
        logger.warn('Network error during session restore — keeping session, will revalidate.');
        enterDegraded();
        setLoading(false);
      });

    // Listen for token expiry events
    const handleTokenExpired = (event: CustomEvent) => {
      logger.log('🔒 Token expired event received:', event.detail);
      const message = event.detail?.message || 'Your session has expired. Please log in again.';
      localStorage.setItem('tokenExpiryMessage', message);
      logout(message);
    };

    window.addEventListener('tokenExpired', handleTokenExpired as EventListener);

    return () => {
      window.removeEventListener('tokenExpired', handleTokenExpired as EventListener);
    };
  }, []);

  // Reconnect watcher — the piece that removes the "restart the app" step.
  // While degraded, poll /api/health with backoff; once the backend answers,
  // revalidate the session and tell the app to refetch. Without this, nothing
  // retries after a deploy finishes and the UI stays empty until a reload.
  useEffect(() => {
    if (!backendDegraded) return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    let attempt = 0;

    const poll = async () => {
      if (cancelled) return;
      try {
        const health = await authenticatedFetch('/api/health');
        if (cancelled) return;

        if (health.ok) {
          const res = await authenticatedFetch('/api/auth/profile');
          if (cancelled) return;
          const data = await res.json().catch(() => null);

          if (res.ok && data?.user) {
            logger.log('✅ Backend reachable again — session revalidated.');
            const freshToken = data.token || null;
            setInMemoryToken(freshToken);
            setToken(freshToken || 'cookie');
            setUser(data.user);
            setTenantSettings(tenantSettingsFromResponse(data));
            safeSetUserCache(data.user);
            setBackendDegraded(false);
            // Let App refetch its data now that we're back.
            window.dispatchEvent(new CustomEvent('backendReconnected'));
            return;
          }

          if (isAuthFailure(res.status, data)) {
            // Backend is up and says the session is dead — now it's decisive.
            setBackendDegraded(false);
            window.dispatchEvent(new CustomEvent('tokenExpired', {
              detail: { message: 'Your session is invalid or expired. Please log in again.' }
            }));
            return;
          }
        }
      } catch {
        /* still down — fall through and retry */
      }

      // 2s → 30s, capped
      const delay = Math.min(2_000 * 2 ** attempt, 30_000);
      attempt += 1;
      timer = setTimeout(poll, delay);
    };

    timer = setTimeout(poll, 2_000);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [backendDegraded]);

  const login = async (username: string, password: string, totpCode?: string): Promise<boolean | 'requires_totp'> => {
    try {
      const data = await apiService.login(username, password, totpCode);
      if (data.requiresTOTP) return 'requires_totp';

      if (data.success && data.token && data.user) {
        // Keep token in memory for the current session; the httpOnly cookie
        // set by the server handles persistence across page reloads.
        setInMemoryToken(data.token);
        setToken(data.token);
        setUser(data.user);
        const ts = tenantSettingsFromResponse(data);
        if (ts !== null) setTenantSettings(ts);
        safeSetUserCache(data.user);
        // The clinic's `preferredLanguage` is only a *default* used to bootstrap
        // a device that has never selected/detected a language. We must NOT
        // override the language the user is already using — doing so flipped the
        // UI to the clinic default (often English) right after login, leaving the
        // session in a mixed-language state. The i18next language detector already
        // persists the active language in localStorage, so respect it here.
        if (data.user.preferredLanguage && !localStorage.getItem('nice-agenda-language')) {
          i18n.changeLanguage(data.user.preferredLanguage);
          localStorage.setItem('nice-agenda-language', data.user.preferredLanguage);
        }
        return true;
      } else {
        logger.error('Login failed:', data.message);
        return false;
      }
    } catch (error) {
      logger.error('Login error:', error);
      return false;
    }
  };

  const loginWithData = (token: string, user: User) => {
    setInMemoryToken(token);
    setToken(token);
    setUser(user);
    safeSetUserCache(user);
    // Pull fresh tenant settings (default reminder flags) after OAuth-style login.
    authenticatedFetch('/api/auth/profile')
      .then(res => res.ok ? res.json() : null)
      .then((data) => {
        if (data?.user) setTenantSettings(tenantSettingsFromResponse(data));
      })
      .catch(() => { /* ignore */ });
  };

  const register = async (userData: RegisterData): Promise<boolean> => {
    try {
      const data = await apiService.register(userData);

      if (data.success && data.token && data.user) {
        setInMemoryToken(data.token);
        setToken(data.token);
        setUser(data.user);
        const ts = tenantSettingsFromResponse(data);
        if (ts !== null) setTenantSettings(ts);
        safeSetUserCache(data.user);
        return true;
      } else {
        logger.error('Registration failed:', data.message);
        return false;
      }
    } catch (error) {
      logger.error('Registration error:', error);
      return false;
    }
  };

  // Wipe every client-side cache that holds user-specific data.
  // Must be called on every logout path to prevent cross-user data leaks.
  const clearSessionCaches = () => {
    // IndexedDB — appointments, patients, procedures, sync queue
    offlineDb.clearAllStores().catch(() => {});
    // Service Worker cache — may hold cached API responses (including /api/auth/profile)
    if (typeof caches !== 'undefined') {
      caches.keys().then(keys => Promise.all(keys.map(k => caches.delete(k)))).catch(() => {});
    }
  };

  const logout = (message?: string) => {
    setInMemoryToken(null);
    setUser(null);
    setToken(null);
    setTenantSettings(null);
    safeRemoveUserCache();
    clearSessionCaches();
    // Ask the server to clear the httpOnly cookie.
    apiService.post('/api/auth/logout').catch(() => {});

    if (message) {
      logger.log('🔒 Logging out due to token expiry:', message);
      showTokenExpiryNotification(message);
    }
  };

  // Function to show a better notification for token expiry
  const showTokenExpiryNotification = (message: string) => {
    // Create a custom notification element
    const notification = document.createElement('div');
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #fef2f2;
      border: 1px solid #fecaca;
      color: #dc2626;
      padding: 16px 20px;
      border-radius: 8px;
      box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
      z-index: 9999;
      max-width: 400px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 14px;
      line-height: 1.5;
    `;
    
    notification.innerHTML = `
      <div style="display: flex; align-items: center; gap: 12px;">
        <div style="font-size: 20px;">🔒</div>
        <div>
          <div style="font-weight: 600; margin-bottom: 4px;">Session Expired</div>
          <div>${message}</div>
        </div>
      </div>
    `;
    
    document.body.appendChild(notification);
    
    // Auto-remove after 5 seconds
    setTimeout(() => {
      if (notification.parentNode) {
        notification.parentNode.removeChild(notification);
      }
    }, 5000);
  };

  // Separate logout function without message for manual logout
  const logoutWithoutMessage = () => {
    setInMemoryToken(null);
    setUser(null);
    setToken(null);
    setTenantSettings(null);
    safeRemoveUserCache();
    clearSessionCaches();
    apiService.post('/api/auth/logout').catch(() => {});
  };

  const refreshTenantSettings = async (): Promise<void> => {
    try {
      const data = await apiService.get('/api/auth/profile');
      if (data?.user) setTenantSettings(tenantSettingsFromResponse(data));
    } catch {
      // non-fatal — stale settings stay until next login
    }
  };

  const updateUser = (patch: Partial<User>) => {
    setUser(prev => prev ? { ...prev, ...patch } : prev);
  };

  const value = {
    user,
    token,
    tenantSettings,
    login,
    loginWithData,
    register,
    logout,
    logoutWithoutMessage,
    updateUser,
    loading,
    backendDegraded,
    subscription,
    refreshSubscription,
    refreshTenantSettings,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
